import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import moment from 'moment';
import { CellAction, CellActionEvent } from "./datatableActionCell.component";

interface StyleConfig {
    [key: string]: any;
}

export enum ColumnKind {
    Text,
    PhoneNumber,
    Email,
    Date,
    Number,
    Link,
    Action
}

export enum ColumnSort {
    None,
    Asc,
    Desc
}

export enum ColumnFilterOp {
    Less,
    LessEqual,
    Equal,
    GreaterEqual,
    Greater
}

export abstract class ColumnDefinition {
    public title: string;
    public abstract get kind(): ColumnKind;
    public getDataValue: (any) => any = (data) => data;
    public sortable: boolean = true;
    public sortType: ColumnSort = ColumnSort.None;
    public filterable: boolean = false;
    public filterTerm: any;
    private minWidth: number;

    public columnStyle: StyleConfig = {
        display: 'flex',
        flexWrap: 'nowrap'
    };

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false, minWidth: number = 0) {
        this.title = title;
        if (formatFn) this.getDataValue = formatFn;
        this.filterable = filterable;
        this.minWidth = minWidth;

        if (this.minWidth && this.minWidth > 0) {
            this.columnStyle.minWidth = `${minWidth}px`;
        }
    }

    public sort(a: any, b: any): number {
        return this.sortWithOrder(a, b, this.sortType);
    }

    public abstract sortWithOrder(a: any, b: any, sortType: ColumnSort): number;

    public filter(data: any): boolean {
        if (this.filterable && this.filterTerm != null) {
            if (this.filterTerm instanceof Array) {
                if (this.filterTerm.length == 0) return false;
                else return this.filterTerm.some(x => this.checkEquality(data, x));
            } else {
                return this.checkEquality(data, this.filterTerm);
            }
        }
        else return true;
    }

    public checkEquality(a: any, b: any): boolean {
        return JSON.stringify(a) == JSON.stringify(b);
    }

    public getLink(data: LinkColumnData): string {
        return data ? data.link : '';
    }

    public getIcon(data: LinkColumnData): string {
        return data ? data.icon : '';
    }
}

export class TextColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.Text; }

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false, maxWidth: number = 0) {
        super(title, formatFn, filterable, maxWidth);
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: return (a || "").toString().localeCompare((b || "").toString());
            case ColumnSort.Desc: return (b || "").toString().localeCompare((a || "").toString());
            default: return 0;
        }
    }
}

export class LinkColumnData {
    public text: string;
    public icon: string;
    public link: string;
    public toString(): string { return this.text; }

    constructor(text: string, link: string, icon: string = null) {
        this.text = text;
        this.link = link;
        this.icon = icon;
    }
}

export class LinkColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.Link; }

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false) {
        super(title, formatFn, filterable);
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: return (a || "").toString().localeCompare((b || "").toString());
            case ColumnSort.Desc: return (b || "").toString().localeCompare((a || "").toString());
            default: return 0;
        }
    }
}

export class PhoneNumberColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.PhoneNumber; }

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false) {
        super(title, formatFn, filterable);
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: {
                try { a = parseInt(a.toString().replace("+", "")); }
                catch { a = 0; }
                try { b = parseInt(b.toString().replace("+", "")); }
                catch { b = 0; }
                return a - b;
            }
            case ColumnSort.Desc: {
                try { a = parseInt(a.toString().replace("+", "")); }
                catch { a = 0; }
                try { b = parseInt(b.toString().replace("+", "")); }
                catch { b = 0; }
                return b - a;
            }
            default: return 0;
        }
    }
}

export class EmailColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.Email; }

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false) {
        super(title, formatFn, filterable);
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: return (a || "").toString().localeCompare((b || "").toString());
            case ColumnSort.Desc: return (b || "").toString().localeCompare((a || "").toString());
            default: return 0;
        }
    }
}

export class DateColumnDefinition extends ColumnDefinition {
    public filterOp: ColumnFilterOp;

    public get kind(): ColumnKind { return ColumnKind.Date; }

    constructor(title: string, formatFn: (any) => any, filterable: boolean = false, maxWidth: number = 0) {
        super(title, formatFn, filterable, maxWidth);
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: {
                let ma = moment(a);
                let mb = moment(b);

                if (!ma.isValid()) return -1;
                if (!mb.isValid()) return 1;

                if (ma.isSame(mb)) return 0;
                else if (ma.isBefore(mb)) return -1;
                else return 1;
            }
            case ColumnSort.Desc: {
                let ma = moment(a);
                let mb = moment(b);

                if (!ma.isValid()) return 1;
                if (!mb.isValid()) return -1;

                if (mb.isSame(ma)) return 0;
                else if (mb.isBefore(ma)) return -1;
                else return 1;
            }
            default: return 0;
        }
    }
}

export class NumberColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.Number; }

    constructor(title: string, formatFn: (any) => any = null, filterable: boolean = false, minWidth: number = 0) {
        super(title, formatFn, filterable, minWidth);

        this.getDataValue = (data) => parseFloat(data.toString());
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        switch (sortType) {
            case ColumnSort.Asc: {
                try { a = parseFloat(a.toString()); }
                catch { a = 0; }
                try { b = parseFloat(b.toString()); }
                catch { b = 0; }
                return a - b;
            }
            case ColumnSort.Desc: {
                try { a = parseFloat(a.toString()); }
                catch { a = 0; }
                try { b = parseFloat(b.toString()); }
                catch { b = 0; }
                return b - a;
            }
            default: return 0;
        }
    }
}

export class ActionColumnData {
    public actions: CellAction[] = [];
    public data: any;

    constructor(actions: CellAction[], data: any) {
        this.actions = actions || [];
        this.data = data;
    }
}

export class ActionColumnDefinition extends ColumnDefinition {
    public get kind(): ColumnKind { return ColumnKind.Action; }

    constructor(title: string) {
        super(title, null, false);
        this.sortable = false;
    }

    public sortWithOrder(a: any, b: any, sortType: ColumnSort): number {
        return 0;
    }
}

export class Style {
    public foregroundColor: string = "black";
    public backgroundColor: string = "white";

    constructor(foregroundColor?: string, backgroundColor?: string) {
        if (foregroundColor)
            this.foregroundColor = foregroundColor;
        if (backgroundColor)
            this.backgroundColor = backgroundColor;
    }
}

export class RowStyle extends Style {
    public cellStyle: CellStyle[] = [];

    constructor(foregroundColor?: string, backgroundColor?: string)
    constructor(foregroundColor: string, backgroundColor: string) {
        super(foregroundColor, backgroundColor);
    }

    public getCellStyle(index: number) {
        let style = this.cellStyle.find(element => element.index == index);
        if (!style)
            style = new CellStyle();
        return style;
    }
}

export class CellStyle extends Style {
    public index: number = -1;
    constructor(foregroundColor?: string, backgroundColor?: string, index?: number) {
        super(foregroundColor, backgroundColor);
        this.index = index;
    }
}

@Component({
    selector: "datatable",
    templateUrl: "./datatable.component.html",
    styleUrls: ["./datatable.component.css"],
    standalone: false
})
export class DatatableComponent implements OnChanges {
    public columnKind = ColumnKind;
    public columnSort = ColumnSort;
    @Input() public columnDefinitions: ColumnDefinition[];
    @Input() public data: any[][];
    @Input() public rowStyleSelector: (row) => RowStyle;
    @Input() public heightHeaderPx: number;
    public outputData: any[][];
    public outputRowStyleSelector: (row) => RowStyle;

    public styleHeader: StyleConfig = {
        height: '24px',
        cursor: 'pointer',
        whiteSpace: 'pre-line'
    };

    @Output() public onCellAction: EventEmitter<CellActionEvent> = new EventEmitter<CellActionEvent>();

    public sortData(event: Event, columnDefinition: ColumnDefinition): void {
        if (columnDefinition.sortable) {
            switch (columnDefinition.sortType) {
                case ColumnSort.Asc: columnDefinition.sortType = ColumnSort.Desc; break;
                case ColumnSort.Desc: columnDefinition.sortType = ColumnSort.Asc; break;
                case ColumnSort.None: columnDefinition.sortType = ColumnSort.Asc; break;
            }

            this.columnDefinitions.forEach((column) => { if (column != columnDefinition) column.sortType = ColumnSort.None; });

            this.updateDataVisualization();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"]) {
            this.updateDataVisualization();
        }
    }

    public filterUpdated() {
        this.updateDataVisualization();
    }

    private updateDataVisualization(): void {
        if (this.data) {

            let sortedData = null;
            let sortColumn = this.columnDefinitions.find(x => x.sortType != ColumnSort.None);
            if (sortColumn) {
                let sortIndex = this.columnDefinitions.indexOf(sortColumn);

                sortedData = this.data.sort((a, b) => sortColumn.sort(a[sortIndex], b[sortIndex]));
            } else {
                sortedData = this.data;
            }

            let filteredData = sortedData;
            let index = 0;
            this.columnDefinitions.forEach(x => {
                filteredData = filteredData.filter((y) => x.filter(y[index]));
                index++;
            })

            this.outputData = filteredData;
            this.outputRowStyleSelector = this.rowStyleSelector || ((x) => new RowStyle("black", "white"));

            if (this.heightHeaderPx) {
                this.styleHeader.height = `${this.heightHeaderPx.toString()}px`;
            }

        } else {
            this.outputData = this.data;
        }
    }

    public clearFilter(columnDefinition: ColumnDefinition): void {
        if (columnDefinition) columnDefinition.filterTerm = '';
        this.updateDataVisualization();
    }

    public raiseOnCellAction(event: CellActionEvent) {
        if (event) {
            this.onCellAction.emit(event);
        }
    }
}
